import { Pencil } from 'lucide-react';
import { useState } from 'react';

import { usePolicy } from '@/react/portainer/policies/queries/usePolicy';
import { AppliedPolicyInfo } from '@/react/portainer/policies/utils/policy-types-metadata';
import { PolicyType, TypedPolicy } from '@/react/portainer/policies/types';
import { getStatusDisplayLabel } from '@/react/portainer/policies/DetailView/AttachmentsDataTables/statusUtils';
import { AppliedPolicyStatusType } from '@/react/portainer/environments/types';
import { ChangeConfirmationPolicyReadOnly } from '@/react/portainer/policies/components/change-confirmation/ChangeConfirmationPolicyReadOnly';

import { Widget, WidgetBody } from '@@/Widget';
import { Badge, BadgeType } from '@@/Badge';
import { LinkButton } from '@@/LinkButton';
import { CollapseExpandButton } from '@@/CollapseExpandButton';
import { TextTip } from '@@/Tip/TextTip';

import { DockerSecurityPolicyReadOnly } from './components/DockerSecurityPolicyReadOnly';
import { DockerSetupPolicyReadOnly } from './components/DockerSetupPolicyReadOnly';
import { DockerRBACPolicyReadOnly } from './components/DockerRBACPolicyReadOnly';
import { DockerRegistryPolicyReadOnly } from './components/DockerRegistryPolicyReadOnly';
import { DockerCleanupPolicyReadOnly } from './components/DockerCleanupPolicyReadOnly';

export interface DockerPolicySectionProps {
  item: AppliedPolicyInfo;
  canEdit: boolean;
}

export function DockerPolicySection({
  item,
  canEdit,
}: DockerPolicySectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const policyId = item.policy?.policyID;
  const policyQuery = usePolicy(policyId!, { enabled: !!policyId });
  if (!item.policy || !policyId) {
    return null;
  }

  const contentId = `policy-section-${policyId}`;

  return (
    <Widget>
      <div className="widget-header w-full p-5!">
        <div className="widget-title flex w-full items-center gap-2">
          <CollapseExpandButton
            isExpanded={isExpanded}
            onClick={() => setIsExpanded((value) => !value)}
            data-cy={`collapse-policy-${policyId}`}
          />
          <button
            type="button"
            className="flex flex-1 items-center gap-2 border-0 bg-transparent p-0 text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-5 focus-visible:ring-offset-1 focus-visible:outline-hidden"
            onClick={() => setIsExpanded((value) => !value)}
            aria-expanded={isExpanded}
            aria-controls={contentId}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.label} ${
              item.policy.policyName || ''
            } policy section`}
          >
            <span className="flex-1">
              {item.label} - {item.policy.policyName || 'Unknown'}
            </span>
            {item.policy.policyStatus?.Status && (
              <Badge type={getStatusBadgeType(item.policy.policyStatus.Status)}>
                {getStatusLabel(item.policy.policyStatus.Status)}
              </Badge>
            )}
          </button>
          {canEdit && (
            <LinkButton
              to="portainer.policy"
              params={{ id: policyId }}
              icon={Pencil}
              size="small"
              color="default"
              data-cy={`edit-policy-button-${policyId}`}
            >
              Edit
            </LinkButton>
          )}
        </div>
      </div>

      <WidgetBody
        loading={policyQuery.isLoading}
        className={isExpanded ? undefined : 'hidden'}
      >
        <div id={contentId} aria-hidden={!isExpanded}>
          {item.policy.policyStatus?.Message && (
            <TextTip
              color={getStatusTipColor(item.policy.policyStatus.Status)}
              className="mb-4"
            >
              {item.policy.policyStatus.Message}
            </TextTip>
          )}

          {policyQuery.isError && (
            <TextTip color="red">
              Failed to load this policy. Please try again.
            </TextTip>
          )}

          {policyQuery.isSuccess && !policyQuery.data && (
            <TextTip color="orange">This policy no longer exists.</TextTip>
          )}

          {policyQuery.data && (
            <PolicyReadOnlyContent
              policy={policyQuery.data}
              policyType={item.type}
            />
          )}
        </div>
      </WidgetBody>
    </Widget>
  );
}

function PolicyReadOnlyContent({
  policy,
  policyType,
}: {
  policy: TypedPolicy<unknown>;
  policyType: PolicyType;
}) {
  switch (policyType) {
    case 'security-docker':
      return <DockerSecurityPolicyReadOnly policy={policy} />;
    case 'setup-docker':
      return <DockerSetupPolicyReadOnly policy={policy} />;
    case 'rbac-docker':
      return <DockerRBACPolicyReadOnly policy={policy} />;
    case 'registry-docker':
      return <DockerRegistryPolicyReadOnly policy={policy} />;
    case 'change-confirmation':
      return <ChangeConfirmationPolicyReadOnly policy={policy} />;
    case 'cleanup-docker':
      return <DockerCleanupPolicyReadOnly policy={policy} />;
    case 'rbac-k8s':
    case 'security-k8s':
    case 'registry-k8s':
    case 'setup-k8s':
    case 'observability-k8s':
    case 'network-security-k8s':
    case 'pod-security-standards-k8s':
    case 'vulnerability-scanning-k8s':
    case 'automatic-namespace-provisioning-k8s':
    default:
      return null;
  }
}

const STATUS_BADGE_TYPE: Partial<Record<AppliedPolicyStatusType, BadgeType>> = {
  [AppliedPolicyStatusType.Applied]: 'success',
  [AppliedPolicyStatusType.Failed]: 'danger',
  [AppliedPolicyStatusType.Warning]: 'warn',
  [AppliedPolicyStatusType.InProgress]: 'info',
  [AppliedPolicyStatusType.NotSupported]: 'muted',
};

function getStatusBadgeType(status: AppliedPolicyStatusType): BadgeType {
  return STATUS_BADGE_TYPE[status] ?? 'muted';
}

function getStatusLabel(status: AppliedPolicyStatusType) {
  switch (status) {
    case AppliedPolicyStatusType.Applied:
    case AppliedPolicyStatusType.Failed:
    case AppliedPolicyStatusType.Warning:
    case AppliedPolicyStatusType.InProgress:
    case AppliedPolicyStatusType.NotSupported:
      return getStatusDisplayLabel(status);
    default:
      return status;
  }
}

function getStatusTipColor(
  status?: AppliedPolicyStatusType
): 'red' | 'orange' | 'blue' | undefined {
  if (!status) return undefined;
  if (status === AppliedPolicyStatusType.Failed) return 'red';
  if (status === AppliedPolicyStatusType.Warning) return 'orange';
  if (status === AppliedPolicyStatusType.InProgress) return 'blue';
  return undefined;
}
